import { upsertMedia } from "apiconfig/api";
import useDeviceInfo from "./useDeviceInfo";
import { v4 as uuidv4 } from "uuid";
import { useState } from "react";
import { getFileExtensionFromMime } from "utils/getFileExtension";

export const convertBlobToFile = (
  blob: Blob,
  fileName: string,
  fallbackMimeType: string = "video/mp4"
) => {
  const extension = getFileExtensionFromMime(blob.type || fallbackMimeType);
  return new File(
    [blob],
    `${fileName}.${extension}`,
    { type: blob.type || fallbackMimeType }
  );
}

const saveMediaMetadata = async (
  id: string, 
  mimeType: string,
  isCamera: boolean, 
  deviceInfo: any,
  fileSizeBytes: number
) => {
  const fileSizeKb = fileSizeBytes / 1024;
  
  const payload = {
    id: id,
    original_file_key: id,
    original_file_mime_type: mimeType,
    upload_size_kb: fileSizeKb,
    metadata: {
      metadata_version: '2025-06-26',
      source: isCamera === true ? "camera" : "photos",
      upload_device: {
        name:
          deviceInfo.device.vendor && deviceInfo.device.model
            ? `${deviceInfo.device.vendor} ${deviceInfo.device.model}`
            : null,
        manufacturer: deviceInfo.device.vendor ?? null,
        model: deviceInfo.device.model ?? null,
        version: deviceInfo.browser.version ?? null,
        os: deviceInfo.os.name ?? null,
        os_version: deviceInfo.os.version ?? null,
      },
    },
  };
  
  const media = await upsertMedia(id, payload);
  return media;
}

const uploadMediaWithPresignedUrl = (
  file: File,
  uploadUrl: string,
  mediaId: string,
  onProgress: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const uploadStartTime = Date.now();
    let lastProgressUpdate = 0;

    xhr.upload.onprogress = async (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);

        // Update progress stats (throttle to avoid excessive API calls - max once per second)
        const now = Date.now();
        if (now - lastProgressUpdate >= 1000) {
          lastProgressUpdate = now;
          
          const elapsedSeconds = (now - uploadStartTime) / 1000;
          const bytesUploaded = event.loaded;
          const bitsUploaded = bytesUploaded * 8;
          const kbps = elapsedSeconds > 0 ? bitsUploaded / elapsedSeconds / 1000 : 0;
          const pctComplete = event.loaded / event.total;

          try {
            await upsertMedia(mediaId, {
              upload_pct_complete: pctComplete,
              upload_avg_kbps: kbps,
            });
          } catch (error) {
            console.error("Error updating upload progress:", error);
            // Don't reject the upload promise, just log the error
          }
        }
      }
    };

    xhr.onload = async () => {
      if ([200, 201, 204].includes(xhr.status)) {
        // Calculate final stats
        const elapsedSeconds = (Date.now() - uploadStartTime) / 1000;
        const totalBits = file.size * 8;
        const finalKbps = elapsedSeconds > 0 ? totalBits / elapsedSeconds / 1000 : 0;

        // Update with final completion status
        try {
          await upsertMedia(mediaId, {
            upload_pct_complete: 1.0,
            upload_avg_kbps: finalKbps,
          });
        } catch (error) {
          console.error("Error updating final upload stats:", error);
        }

        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Upload failed due to a network error"));
    };

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
};

const useMediaUpload = () => {
  const [uploadState, setUploadState] = useState<"not-started" | "uploading" | "uploaded" | "error">("not-started");
  const [uploadProgress, setUploadProgress] = useState(0.0);
  const deviceInfo = useDeviceInfo();

  const uploadMedia = async (
    file: File,
    isFromCamera: boolean
  ) => {
    setUploadState("uploading");
    setUploadProgress(0.0);

    const mediaId = uuidv4();
    try {
      const media = await saveMediaMetadata(mediaId, file.type, isFromCamera, deviceInfo, file.size);
      await uploadMediaWithPresignedUrl(file, media?.data?.uploadUrl, mediaId, setUploadProgress);
      setUploadState("uploaded");
      return mediaId;
    } catch (error) {
      setUploadState("error");
      throw error;
    }
  }

  return { 
    uploadState,
    uploadProgress,
    uploadMedia, 
    convertBlobToFile
  };
}



export default useMediaUpload;